import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Mail, Sparkles, Archive, Trash2, Pencil, FileSearch, Loader2, FileText, Save, BookOpen, Wand2, Gauge, Layers, CheckCircle2, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea, Select } from "@/components/FormBits";
import { api, API, fileUrl, pdfUrl, setToken } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { FLOWS, ENTRY_TYPES } from "@/config/entryFlows";

const STATUSES = ["Draft", "Submitted", "Approved", "Published", "Archived"];
const IMPACT_TAGS = ["Environment", "People", "Governance", "Community", "Safety", "Innovation"];

function readPath(obj, path) {
  if (!path) return "";
  return path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj) ?? "";
}

function recordTitle(p) {
  const td = p?.type_data || {};
  return (
    p?.project_name || td.initiative_name || td.award_name || td.event_name ||
    td.certification_name || td.title || ""
  );
}

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [notes, setNotes] = useState("");
  const [quality, setQuality] = useState(null);
  const [submitterAvatar, setSubmitterAvatar] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/projects/${id}`);
      setP(data);
      setNotes(data.internal_notes || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    api.get(`/admin/projects/${id}/quality-score`).then((r) => setQuality(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!p?.owner_user_id) return;
    api.get("/users/avatars").then((r) => setSubmitterAvatar((r.data || {})[p.owner_user_id] || null)).catch(() => {});
  }, [p?.owner_user_id]);

  const setStatus = async (status) => {
    setBusyAction("status");
    try {
      const { data } = await api.post(`/admin/projects/${id}/status`, { status });
      setP(data);
      toast.success(`Status set to ${status}`);
    } finally {
      setBusyAction("");
    }
  };

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackMsg, setSendBackMsg] = useState("");

  const approve = async () => {
    setBusyAction("approve");
    try {
      const { data } = await api.post(`/admin/projects/${id}/approve`);
      setP(data);
      toast.success(`Approved — ${p.submitter_name || "the submitter"} has been emailed`);
    } catch {
      toast.error("Could not approve");
    } finally { setBusyAction(""); }
  };

  const toggleImpactTag = async (tag) => {
    const cur = p.impact_tags || [];
    const next = cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag];
    setP((prev) => ({ ...prev, impact_tags: next }));
    try {
      await api.patch(`/admin/projects/${id}`, { impact_tags: next });
    } catch {
      toast.error("Could not save impact tags");
    }
  };

  const sendBack = async () => {
    setBusyAction("sendback");
    try {
      const { data } = await api.post(`/admin/projects/${id}/send-back`, { message: sendBackMsg });
      setSendBackOpen(false);
      setSendBackMsg("");
      toast.success(data.email_sent
        ? `Sent back to ${p.submitter_name || "the submitter"} with your note`
        : "Returned to Draft (no submitter email on record)");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send back");
    } finally { setBusyAction(""); }
  };

  const downloadPdf = async () => {
    setBusyAction("pdf");
    try {
      const token = localStorage.getItem("rcw_token");
      const res = await fetch(`${API}/admin/projects/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`PDF request failed (${res.status})`);
      const blob = await res.blob();
      if (!blob.type.includes("pdf")) throw new Error("Server did not return a PDF");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `RCWilliams-${(p.project_name || "project").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("PDF downloaded — check your Downloads folder");
    } catch (e) {
      // Fallback: open the tokenized URL directly so the browser handles it.
      try {
        window.open(pdfUrl(`/admin/projects/${id}/pdf`), "_blank", "noopener");
        toast.info("Opening PDF in a new tab");
      } catch {
        toast.error("PDF download failed");
      }
    } finally {
      setBusyAction("");
    }
  };

  const downloadCaseStudyPdf = async () => {
    setBusyAction("csPdf");
    try {
      const { data } = await api.post(`/admin/projects/${id}/email-case-study`);
      const to = (data?.to || []).join(", ");
      toast.success(`Case study emailed to ${to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not email case study");
    } finally {
      setBusyAction("");
    }
  };

  const resend = async () => {
    setBusyAction("resend");
    try {
      await api.post(`/admin/projects/${id}/resend-pdf`);
      toast.success("PDF email queued");
    } finally {
      setBusyAction("");
    }
  };

  const generateCaseStudy = async () => {
    setBusyAction("cs");
    try {
      const { data } = await api.post(`/admin/projects/${id}/generate-case-study`);
      setP((prev) => ({ ...prev, case_study_draft: data }));
      toast.success("Case study draft generated");
    } catch (e) {
      const d = e?.response?.data?.detail || "Generation failed";
      toast.error(typeof d === "string" ? d : "Generation failed");
    } finally {
      setBusyAction("");
    }
  };

  const archive = async () => {
    setBusyAction("archive");
    try {
      await api.post(`/admin/projects/${id}/archive`);
      toast.success("Archived");
      navigate("/admin");
    } finally {
      setBusyAction("");
    }
  };

  const deleteProject = async () => {
    const confirmed = window.confirm(
      `Delete "${recordTitle(p) || "this record"}" permanently? This removes the record and all uploaded files. This cannot be undone.`
    );
    if (!confirmed) return;
    setBusyAction("delete");
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      navigate("/admin");
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusyAction("");
    }
  };

  const saveNotes = async () => {
    setBusyAction("notes");
    try {
      await api.patch(`/admin/projects/${id}`, { internal_notes: notes });
      toast.success("Notes saved");
    } finally {
      setBusyAction("");
    }
  };

  if (loading || !p) {
    return (
      <Layout>
        <div className="py-20 text-center text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      </Layout>
    );
  }

  const images = (p.files || []).filter((f) => !f.is_deleted && (f.content_type || "").startsWith("image/"));
  const otherFiles = (p.files || []).filter((f) => !f.is_deleted && !(f.content_type || "").startsWith("image/"));
  const canEdit = isAdmin || p.owner_user_id === user?.id;
  const entryType = (p.entry_type || "project").toLowerCase();
  const isProject = entryType === "project";
  const td = p.type_data || {};
  const typeLabel = ENTRY_TYPES[entryType]?.label || entryType;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Link to="/admin" className="rcw-btn-ghost text-sm mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="rcw-label">
              {isProject
                ? `${p.client_name || "—"} · ${p.location || "—"}`
                : `${typeLabel}${td.location || p.location ? ` · ${td.location || p.location}` : ""}`}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-1 text-gray-900 tracking-tight">
              {recordTitle(p) || "Untitled project"}
            </h1>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
              {p.year_completed ? <span>Year: {p.year_completed}</span> : null}
              {p.project_value ? <span>· £{String(p.project_value).replace(/^£/, "")}</span> : null}
              {(p.categories || []).length ? <span>· {(p.categories || []).join(", ")}</span> : null}
            </div>
            {quality && <QualityScoreBadge quality={quality} />}
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {canEdit && (
              <div className="col-span-2 sm:col-span-1 flex items-center gap-2">
                <Select options={STATUSES} value={p.status || ""} onChange={(e) => setStatus(e.target.value)} data-testid={ADMIN.changeStatus} />
              </div>
            )}
            {isAdmin && p.status === "Submitted" && (
              <button onClick={approve} disabled={busyAction === "approve"} className="rcw-btn-amber text-sm" data-testid="approve-project-btn">
                {busyAction === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve
              </button>
            )}
            {isAdmin && p.status === "Submitted" && (
              <button onClick={() => setSendBackOpen(true)} className="rcw-btn-ghost text-sm" data-testid="send-back-btn">
                <Undo2 className="h-4 w-4" /> Send back
              </button>
            )}
            {isAdmin && (
              <div className="col-span-2 sm:w-full flex flex-wrap items-center gap-1.5 pt-1" data-testid="impact-tags">
                <span className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mr-1">Impact</span>
                {IMPACT_TAGS.map((t) => {
                  const on = (p.impact_tags || []).includes(t);
                  return (
                    <button key={t} onClick={() => toggleImpactTag(t)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        on ? "bg-gray-900 border-gray-900 text-amber-300" : "bg-white border-gray-200 text-gray-500 hover:border-gray-400"
                      }`}
                      data-testid={`impact-tag-${t.toLowerCase()}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="col-span-2 sm:w-full space-y-2 pt-1">
              <ActionGroup label="Record">
                {canEdit && (
                  <Link to={`/admin/projects/${id}/edit`} className="rcw-btn-ghost text-sm" data-testid="admin-edit-project">
                    <Pencil className="h-4 w-4" /> Edit details
                  </Link>
                )}
                {canEdit && (
                  <button onClick={archive} disabled={busyAction === "archive"} className="rcw-btn-ghost text-sm" data-testid={ADMIN.archive}>
                    <Archive className="h-4 w-4" /> Archive
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={deleteProject}
                    disabled={busyAction === "delete"}
                    className="rcw-btn-ghost text-sm text-red-600 hover:bg-red-50"
                    data-testid="admin-delete-project"
                  >
                    {busyAction === "delete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete
                  </button>
                )}
              </ActionGroup>
              <ActionGroup label="Submission record" hint="Plain PDF of everything that was submitted — for internal records">
                <Link to={`/admin/projects/${id}/preview`} className="rcw-btn-ghost text-sm" data-testid="admin-pdf-preview">
                  <FileSearch className="h-4 w-4" /> View
                </Link>
                <button onClick={downloadPdf} disabled={busyAction === "pdf"} className="rcw-btn-ghost text-sm" data-testid={ADMIN.downloadPdf}>
                  {busyAction === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
                </button>
                {canEdit && (
                  <button onClick={resend} disabled={busyAction === "resend"} className="rcw-btn-ghost text-sm" data-testid={ADMIN.resendPdf}>
                    {busyAction === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me a copy
                  </button>
                )}
              </ActionGroup>
              <ActionGroup label="Case study & content" hint="Designed one-page PDF plus AI-written posts, ready to share">
                {canEdit && (
                  <button onClick={generateCaseStudy} disabled={busyAction === "cs"} className="rcw-btn-amber text-sm" data-testid={ADMIN.generateCaseStudy}>
                    {busyAction === "cs" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Write draft with AI
                  </button>
                )}
                {canEdit && (p.entry_type || "project") === "project" && (
                  <Link
                    to={`/admin/projects/${id}/case-study`}
                    className="rcw-btn-ghost text-sm"
                    data-testid="admin-case-study-edit"
                  >
                    <Wand2 className="h-4 w-4" /> Edit content
                  </Link>
                )}
                <button onClick={downloadCaseStudyPdf} disabled={busyAction === "csPdf"} className="rcw-btn-ghost text-sm" data-testid="admin-case-study-pdf">
                  {busyAction === "csPdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />} Case study PDF
                </button>
                <Link
                  to={`/admin/projects/${id}/outputs`}
                  className="rcw-btn-ghost text-sm"
                  data-testid="admin-outputs"
                >
                  <Layers className="h-4 w-4" /> Social posts & outputs
                </Link>
              </ActionGroup>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
          <div className="lg:col-span-2 space-y-5">
            <Section title="Submitted by">
              <div className="flex items-center gap-3 mb-2">
                <Avatar name={p.submitter_name} src={submitterAvatar} size="md" data-testid="detail-submitter-avatar" />
                <div>
                  <div className="text-sm font-medium text-gray-900">{p.submitter_name || "—"}</div>
                  <div className="text-xs text-gray-500">{p.submitter_email || ""}</div>
                </div>
              </div>
              <KV k="Submitted at" v={(p.submitted_at || "").slice(0, 19)} />
            </Section>

            {!isProject && FLOWS[entryType] && (
              <TypeDataSections p={p} flow={FLOWS[entryType]} />
            )}

            {isProject && (<>
            <Section title="Project summary">
              <KV k="One sentence" v={p.one_sentence} />
              <KV k="Brief description" v={p.brief_description} />
              <KV k="Why needed" v={p.why_needed} />
              <KV k="Who was affected" v={(p.who_affected || []).join(", ")} />
              <KV k="Additional info" v={p.additional_summary_info} />
            </Section>

            <Section title="The challenge">
              <KV k="Selected" v={(p.challenges || []).join(", ")} />
              <KV k="Main challenge" v={p.main_challenge_text} />
              <KV k="Other details" v={p.other_challenge_text} />
            </Section>

            <Section title="The solution">
              <KV k="Selected" v={(p.solutions || []).join(", ")} />
              <KV k="Works carried out" v={p.works_carried_out} />
              <KV k="Materials used" v={p.materials_used} />
              <KV k="Specialist methods" v={p.specialist_methods} />
              <KV k="Temporary works" v={p.temporary_works} />
              <KV k="Environmental controls" v={p.environmental_controls} />
              <KV k="Extra" v={p.extra_solution} />
            </Section>

            <Section title="The outcome">
              <KV k="Selected" v={(p.outcomes || []).join(", ")} />
              <KV k="Main result" v={p.main_result} />
              <KV k="Client/public benefit" v={p.client_public_benefit} />
              <KV k="Delivered safely" v={p.delivered_safely} />
              <KV k="Delivered on time" v={p.delivered_on_time} />
              <KV k="Extra" v={p.extra_outcome} />
            </Section>

            <Section title="Key facts">
              <KV k="Length of project" v={p.duration} />
              <KV k="Area" v={p.area} />
              <KV k="Materials summary" v={p.materials_summary} />
              <KV k="Tonnes" v={p.tonnes} />
              <KV k="Assets" v={p.assets_count} />
              <KV k="Operatives" v={p.operatives} />
              <KV k="Plant used" v={p.plant_used} />
              <KV k="Traffic management" v={p.traffic_management} />
              <KV k="Environmental measures" v={p.environmental_measures} />
              <KV k="Useful numbers" v={p.useful_numbers} />
              <KV k="Awards" v={p.awards} />
              {(p.custom_facts || []).map((cf) => (
                <KV key={cf.id} k={cf.label || "Custom fact"} v={cf.value} />
              ))}
            </Section>

            <Section title="Final notes & approval">
              <KV k="Most proud of" v={p.proud_of} />
              <KV k="Unusual/impressive" v={p.unusual_impressive} />
              <KV k="Avoid publicly" v={p.avoid_publicly} />
              <KV k="Can be used publicly" v={p.public_use} />
              <KV k="Client approval needed" v={p.client_approval} />
            </Section>
            </>)}

            {(p.custom_fields || []).length > 0 && (
              <Section title={`Custom fields (${(p.custom_fields || []).length})`}>
                {Object.entries(
                  (p.custom_fields || []).reduce((acc, cf) => {
                    const k = cf.section || "Other";
                    acc[k] = acc[k] || [];
                    acc[k].push(cf);
                    return acc;
                  }, {})
                ).map(([sec, list]) => (
                  <div key={sec} className="mt-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600">{sec}</div>
                    <div className="mt-1 space-y-1">
                      {list.map((cf) => (
                        <KV
                          key={cf.id}
                          k={cf.label}
                          v={
                            cf.type === "checkbox"
                              ? cf.value
                                ? "Yes"
                                : "No"
                              : Array.isArray(cf.value)
                              ? cf.value.join(", ")
                              : String(cf.value ?? "")
                          }
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </Section>
            )}

            {images.length > 0 && (
              <Section title={`Photos (${images.length})`}>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {images.map((f) => (
                    <a key={f.storage_path} href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="block">
                      <div className="aspect-video bg-gray-100 rounded-md overflow-hidden">
                        <img src={fileUrl(f.storage_path)} alt={f.caption || ""} className="w-full h-full object-cover" />
                      </div>
                      <div className="text-xs text-gray-500 mt-1 truncate">{f.caption || f.category}</div>
                    </a>
                  ))}
                </div>
              </Section>
            )}

            {otherFiles.length > 0 && (
              <Section title={`Files (${otherFiles.length})`}>
                <ul className="divide-y divide-gray-100">
                  {otherFiles.map((f) => (
                    <li key={f.storage_path} className="py-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                        <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer" className="text-sm truncate hover:underline">
                          {f.original_filename}
                        </a>
                      </div>
                      <span className="text-xs text-gray-500 shrink-0">{f.category}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          <div className="space-y-5">
            <Section title="Internal notes">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Admin only notes…" data-testid="admin-internal-notes" />
              <button onClick={saveNotes} disabled={busyAction === "notes"} className="mt-3 rcw-btn-primary text-sm">
                <Save className="h-4 w-4" /> Save notes
              </button>
            </Section>

            {p.case_study_draft && (
              <Section title="Case study draft">
                <CaseStudyView draft={p.case_study_draft} />
              </Section>
            )}
          </div>
        </div>
      </div>
      {sendBackOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="send-back-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">Send back to {p.submitter_name || "submitter"}</h3>
              <button onClick={() => setSendBackOpen(false)} className="p-1 text-gray-400 hover:text-gray-700" data-testid="send-back-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              They'll get an email with your note and a link to reopen and finish the submission. Status returns to Draft.
            </p>
            <Textarea
              rows={4}
              className="mt-3"
              value={sendBackMsg}
              onChange={(e) => setSendBackMsg(e.target.value)}
              placeholder="e.g. Can you add the project name, the client, and a few lines on what was done?"
              data-testid="send-back-message"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSendBackOpen(false)} className="rcw-btn-ghost text-sm" data-testid="send-back-cancel">Cancel</button>
              <button onClick={sendBack} disabled={busyAction === "sendback" || !sendBackMsg.trim()} className="rcw-btn-amber text-sm" data-testid="send-back-confirm">
                {busyAction === "sendback" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Send back
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function ActionGroup({ label, hint, children }) {
  const kids = React.Children.toArray(children).filter(Boolean);
  if (!kids.length) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold" title={hint}>{label}</div>
      {hint && <div className="text-[11px] text-gray-400 mb-1.5">{hint}</div>}
      <div className="flex flex-wrap gap-2 mt-1">{kids}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rcw-card p-5 sm:p-6">
      <div className="rcw-label">{title}</div>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function TypeDataSections({ p, flow }) {
  const shownKeys = new Set();
  const sections = flow.steps
    .map((s, idx) => {
      const rows = (s.fields || [])
        .filter((f) => !f.key.startsWith("submitter_"))
        .map((f) => {
          shownKeys.add(f.key.replace(/^type_data\./, ""));
          return [f.label, readPath(p, f.key)];
        })
        .filter(([, v]) => String(v || "").trim());
      // step 0 is the "Get started" step — its name field already shows as the page title
      return { title: s.title, rows: idx === 0 ? [] : rows };
    })
    .filter((s) => s.rows.length);
  const extras = Object.entries(p.type_data || {})
    .filter(([k, v]) => !shownKeys.has(k) && String(v || "").trim())
    .map(([k, v]) => [k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()), v]);
  return (
    <>
      {sections.map((s) => (
        <Section key={s.title} title={s.title}>
          {s.rows.map(([k, v]) => <KV key={k} k={k} v={String(v)} />)}
        </Section>
      ))}
      {extras.length > 0 && (
        <Section title="Other details">
          {extras.map(([k, v]) => <KV key={k} k={k} v={String(v)} />)}
        </Section>
      )}
    </>
  );
}
function KV({ k, v }) {
  if (!v) return null;
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="text-gray-500">{k}</div>
      <div className="col-span-2 text-gray-900 whitespace-pre-wrap break-words">{v}</div>
    </div>
  );
}

function CaseStudyView({ draft }) {
  const labels = draft.section_labels || {};
  const fields = [
    ["Suggested title", draft.suggested_title],
    ["Website title", draft.website_title],
    ["Short intro", draft.short_intro],
    ["Overview", draft.overview],
    [labels.challenge || "Challenge", draft.challenge],
    [labels.solution || "Solution", draft.solution],
    [labels.outcome || "Outcome", draft.outcome],
    ["Key facts", draft.key_facts],
    ["LinkedIn post", draft.linkedin_post],
    ["Facebook post", draft.facebook_post],
    ["Meta title", draft.meta_title],
    ["Meta description", draft.meta_description],
  ];
  return (
    <div className="space-y-3 text-sm">
      {fields.map(([k, v]) =>
        v ? (
          <div key={k}>
            <div className="rcw-label">{k}</div>
            <div className="mt-1 text-gray-900 whitespace-pre-wrap break-words">{v}</div>
          </div>
        ) : null
      )}
      {(draft.image_captions || []).length > 0 && (
        <div>
          <div className="rcw-label">Image captions</div>
          <ol className="mt-1 list-decimal list-inside space-y-1">
            {draft.image_captions.map((c, i) => (
              <li key={i} className="text-gray-900">{c}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function QualityScoreBadge({ quality }) {
  const { score, band, suggestions = [] } = quality;
  const color = score >= 80 ? "emerald" : score >= 60 ? "amber" : score >= 40 ? "orange" : "red";
  const ringPct = Math.max(2, Math.min(100, score));
  const palette = {
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "#10B981" },
    amber: { bg: "bg-amber-50", text: "text-amber-800", ring: "#F59E0B" },
    orange: { bg: "bg-orange-50", text: "text-orange-800", ring: "#F97316" },
    red: { bg: "bg-red-50", text: "text-red-800", ring: "#EF4444" },
  }[color];
  return (
    <div className={`mt-4 inline-flex items-start gap-3 p-3 pr-4 rounded-xl ${palette.bg} max-w-2xl`} data-testid="quality-score">
      <div
        className="relative h-14 w-14 shrink-0 rounded-full"
        style={{ background: `conic-gradient(${palette.ring} ${ringPct}%, #E5E7EB 0)` }}
        aria-label={`Quality score ${score}/100`}
      >
        <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
          <span className={`font-bold text-lg ${palette.text}`}>{score}</span>
        </div>
      </div>
      <div className="min-w-0">
        <div className={`text-xs font-bold uppercase tracking-wider ${palette.text}`}>
          Submission quality · {band}
        </div>
        {suggestions.length === 0 ? (
          <div className="text-sm text-gray-700 mt-1">Looking sharp — nothing missing.</div>
        ) : (
          <ul className="text-xs text-gray-700 mt-1 space-y-0.5 list-disc list-inside">
            {suggestions.slice(0, 4).map((s, i) => <li key={i}>{s}</li>)}
            {suggestions.length > 4 && <li className="text-gray-500">+{suggestions.length - 4} more</li>}
          </ul>
        )}
      </div>
    </div>
  );
}

